import { useState, useEffect, useCallback } from 'react';
import { attendanceService } from '../services/attendanceService';

export const useAttendance = (params = {}) => {
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const fetchAttendance = useCallback(async (fetchParams = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await attendanceService.getMyAttendance({
        ...params,
        ...fetchParams
      });
      
      // Handle different response structures
      const attendanceData = response.data?.data?.attendance || response.data?.data || [];
      setAttendance(Array.isArray(attendanceData) ? attendanceData : []);
      
      if (response.data?.data?.pagination) {
        setPagination(response.data.data.pagination);
      } else if (response.data?.pagination) {
        setPagination(response.data.pagination);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch attendance');
      console.error('Error fetching attendance:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAttendance();
  }, []);

  const markAttendance = async (data) => {
    try {
      const response = await attendanceService.markAttendance(data);
      await fetchAttendance(); // Refresh the list
      return response.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to mark attendance';
    }
  };

  const updateAttendance = async (attendanceId, data) => {
    try {
      const response = await attendanceService.updateAttendance(attendanceId, data);
      await fetchAttendance(); // Refresh the list
      return response.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to update attendance';
    }
  };

  const bulkMarkAttendance = async (data) => {
    try {
      const response = await attendanceService.bulkMarkAttendance(data);
      await fetchAttendance(); // Refresh the list
      return response.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to mark attendance';
    }
  };

  const refreshAttendance = () => {
    fetchAttendance();
  };

  const loadMore = () => {
    if (pagination.page < pagination.totalPages) {
      fetchAttendance({ page: pagination.page + 1 });
    }
  };

  return {
    attendance,
    loading,
    error,
    pagination,
    markAttendance,
    updateAttendance,
    bulkMarkAttendance,
    refreshAttendance,
    loadMore,
    fetchAttendance
  };
};

export const useStudentAttendance = (studentId, params = {}) => {
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStudentAttendance = useCallback(async () => {
    if (!studentId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await attendanceService.getStudentAttendance(studentId, params);
      setAttendance(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch student attendance');
      console.error('Error fetching student attendance:', err);
    } finally {
      setLoading(false);
    }
  }, [studentId, params]);

  useEffect(() => {
    fetchStudentAttendance();
  }, [fetchStudentAttendance]);

  return {
    attendance,
    loading,
    error,
    refreshStudentAttendance: fetchStudentAttendance
  };
};

export const useClassAttendance = (classId, params = {}) => {
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchClassAttendance = useCallback(async () => {
    if (!classId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await attendanceService.getClassAttendance(classId, params);
      setAttendance(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch class attendance');
      console.error('Error fetching class attendance:', err);
    } finally {
      setLoading(false);
    }
  }, [classId, params]);

  useEffect(() => {
    fetchClassAttendance();
  }, [fetchClassAttendance]);

  return {
    attendance,
    loading,
    error,
    refreshClassAttendance: fetchClassAttendance
  };
};

export const useAttendanceStats = (params = {}) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAttendanceStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await attendanceService.getAttendanceStats(params);
      setStats(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch attendance statistics');
      console.error('Error fetching attendance statistics:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAttendanceStats(params);
  }, [fetchAttendanceStats]);

  return {
    stats,
    loading,
    error,
    refreshAttendanceStats: fetchAttendanceStats
  };
};

export const useAttendanceCalendar = (studentId, year, month) => {
  const [calendar, setCalendar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAttendanceCalendar = useCallback(async () => {
    if (!studentId || !year || !month) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await attendanceService.getAttendanceCalendar(studentId, year, month);
      setCalendar(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch attendance calendar');
      console.error('Error fetching attendance calendar:', err);
    } finally {
      setLoading(false);
    }
  }, [studentId, year, month]);

  useEffect(() => {
    fetchAttendanceCalendar();
  }, [fetchAttendanceCalendar]);

  return {
    calendar,
    loading,
    error,
    refreshAttendanceCalendar: fetchAttendanceCalendar
  };
};
